import {
  faBullseyeArrow,
  faMobileAndroid,
  faUnlock,
} from "@fortawesome/duotone-light-svg-icons"
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CustomRewardCurrency,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { CloseButton } from "../../../components/closeButton/CloseButton"
import TransitionWrapper from "../../../components/coaching/TransitionWrapper"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import {
  QUERY_KEYS,
  ScreenTimeControlsAnalyticsStep,
  ScreenTimeControlsModalStep,
  screenTimeFTUXItems,
} from "../../../constants"
import useMobile from "../../../hooks/useMobile"
import { useFamilyQuery, useUserQuery } from "../../../networking/queries"
import { useCustomRewardsQuery } from "../../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../../types"
import { ScreenTimeControlsModalType } from "../../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { queryClient } from "../../../util/queryClient"
import { localStorageItems } from "../../../util/storage"
import { getScreenTimeConfigurations, preloadImage } from "../../../util/util"
import { useGetChildStartedModal } from "../../family/GetChildStartedModal"
import ManagePasscodeSection from "../../family/parentUser/ManagePasscodeSection"
import useAddEditRewardModalStore from "../hooks/useAddEditRewardModalStore"

// Store

interface ScreenTimeControlsModalStoreProps {
  isOpen: boolean
  onOpen: ({
    step,
    modalType,
    rewardId,
  }: {
    step: ScreenTimeControlsModalStep
    modalType: ScreenTimeControlsModalType
    rewardId?: number | null
  }) => void
  onClose: () => void
  step: ScreenTimeControlsModalStep
  setStep: (step: ScreenTimeControlsModalStep) => void
  modalType: ScreenTimeControlsModalType
  rewardId: number | null
  setRewardId: (rewardId: number | null) => void
  isIos: boolean
  setIsIos: (isIos: boolean) => void
}

export const useScreenTimeControlsModalStore =
  create<ScreenTimeControlsModalStoreProps>()((set) => ({
    isOpen: false,
    onOpen: ({
      step,
      modalType,
      rewardId,
    }: {
      step: ScreenTimeControlsModalStep
      modalType: ScreenTimeControlsModalType
      rewardId?: number | null
    }) => set({ isOpen: true, step, modalType, rewardId: rewardId ?? null }),
    onClose: () => {
      localStorage.setItem(
        localStorageItems.hasSeenScreenTimeControlsModal,
        "true"
      )
      set({ isOpen: false, step: ScreenTimeControlsModalStep.INTRO })
    },
    step: ScreenTimeControlsModalStep.ANNOUNCEMENT,
    setStep: (step: ScreenTimeControlsModalStep) => set({ step }),
    modalType: ScreenTimeControlsModalType.REWARDS_DETAILS,
    rewardId: null,
    setRewardId: (rewardId: number | null) => set({ rewardId }),
    isIos: false,
    setIsIos: (isIos: boolean) => set({ isIos }),
  }))

// Screen Time Controls Modal

const FTUX_ITEM_KEYS = [
  { title: "STCM_FTUX_1_TITLE", description: "STCM_FTUX_1_DESC" },
  { title: "STCM_FTUX_2_TITLE", description: "STCM_FTUX_2_DESC" },
  { title: "STCM_FTUX_3_TITLE", description: "STCM_FTUX_3_DESC" },
] as const

export const ScreenTimeControlsModal = () => {
  const { isOpen, onClose, step, setStep, modalType } =
    useScreenTimeControlsModalStore()
  const { t } = useTranslation()

  const isMobile = useMobile()

  const isFirstStep =
    step === ScreenTimeControlsModalStep.ANNOUNCEMENT ||
    step === ScreenTimeControlsModalStep.INTRO

  const showFinishSetUpText =
    modalType === ScreenTimeControlsModalType.REWARDS_TAB ||
    modalType === ScreenTimeControlsModalType.POST_CREATE

  const showHeader = !isFirstStep && step !== ScreenTimeControlsModalStep.FINISH

  const isFullHeight = isMobile && !isFirstStep

  const showBackButton = step !== ScreenTimeControlsModalStep.SETUP_PASSCODE

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size={"fit-content"}
      backgroundType="overlay"
    >
      <div
        className="w-full flex flex-col justify-start"
        style={{
          background: JoonUIColor.background.xlightGray,
          ...(isFullHeight
            ? {
                height: "95vh",
                marginTop: "auto",
              }
            : {
                height: "100%",
              }),
        }}
      >
        {showHeader ? (
          <div
            className={`w-full flex flex-row justify-between items-center border-b border-borderPrimary bg-white ${
              showBackButton ? "pl-4" : "pl-6"
            } pr-2 py-2 h-[50px]`}
          >
            <div className="w-full flex flex-row justify-start items-center gap-1">
              {showBackButton && (
                <TextButton onClick={() => setStep(step - 1)}>
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    color={JoonUIColor.icon.neutral}
                    fontSize={SPACING.space4}
                  />
                </TextButton>
              )}
              <Typography variant="h3">
                {showFinishSetUpText
                  ? t("STCM_HEADER_FINISH")
                  : t("STCM_HEADER_SETUP")}
              </Typography>
            </div>
            <CloseButton
              onClick={onClose}
              style={{ position: "relative", top: "unset", right: "unset" }}
            />
          </div>
        ) : (
          <CloseButton onClick={onClose} style={{ top: "13px" }} />
        )}
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.ANNOUNCEMENT}
          style={{
            width: "100%",
          }}
        >
          <AnnouncementStep />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.INTRO}
          style={{
            width: "100%",
          }}
        >
          <IntroStep />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.SETUP_PASSCODE}
          style={{
            width: "100%",
          }}
        >
          <SetupPasscodeStep />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.DEVICE_SELECTION}
          style={{
            width: "100%",
          }}
        >
          <DeviceSelectionStep />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.SETUP_JOONITY}
          style={{
            width: "100%",
          }}
        >
          <SetupJoonityStep />
        </TransitionWrapper>
        <TransitionWrapper
          showChildren={step === ScreenTimeControlsModalStep.FINISH}
          style={{
            width: "100%",
            height: step === ScreenTimeControlsModalStep.FINISH ? "100%" : "0%",
          }}
        >
          <FinishStep />
        </TransitionWrapper>
      </div>
    </SlideUpModal>
  )
}

// Steps

const AnnouncementStep = () => {
  const { onClose } = useScreenTimeControlsModalStore()
  const {
    selectedChildren,
    onOpen: onOpenAddEditRewardModal,
    setCurrency,
    setEmoji,
    setCost,
    setTitle,
    setSelectedChildren,
  } = useAddEditRewardModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { t } = useTranslation()

  const onCreateReward = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_FTUX_CREATE_REWARD)
    if (children?.[0]?.user?.id && selectedChildren.length === 0)
      setSelectedChildren([children?.[0]?.user?.id])
    setCurrency(CustomRewardCurrency.MINUTE)
    setCost(4)
    setTitle(t("AERM_DEFAULT_SCREEN_TIME"))
    setEmoji("📱")
    // note: there is also a useEffect in AddEditRewardModal that sets the same stuff
    onOpenAddEditRewardModal()
    onClose()
  }

  return (
    <div className="w-full flex flex-col">
      <ScreenTimeControlsImage />

      <div className="flex flex-col gap-4 w-full p-6">
        <AvaliableOnAndroidCard />

        <Typography variant="h3">{t("STCM_ANN_TITLE")}</Typography>

        {screenTimeFTUXItems.map((item, index) => (
          <div key={index} className="flex flex-row gap-1">
            <FontAwesomeIcon
              icon={item.icon}
              color={JoonUIColor.icon.accent}
              fontSize={SPACING.space4}
            />
            <div key={index} className="flex flex-col">
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.primary}
              >
                {t(FTUX_ITEM_KEYS[index].title)}
              </Typography>
              <Typography variant="bodySmall" color={JoonUIColor.text.primary}>
                {t(FTUX_ITEM_KEYS[index].description)}
              </Typography>
            </div>
          </div>
        ))}
        <div className="w-full flex flex-col gap-2 justify-center items-center">
          <Button
            text={t("STCM_ANN_CTA")}
            onClick={onCreateReward}
            fullWidth
            style={{ position: "sticky", bottom: SPACING.space2 }}
          />
          <TextButton
            onClick={() => {
              trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_FTUX_DISMISS)
              onClose()
            }}
            style={{ width: "fit-content" }}
          >
            <Typography
              variant="bodyBold"
              color={JoonUIColor.text.primaryAccent}
            >
              {t("STR_FTUX_MAYBE_LATER")}
            </Typography>
          </TextButton>
        </div>
      </div>
    </div>
  )
}

const IntroStep = () => {
  const { setStep, modalType, onClose, rewardId } =
    useScreenTimeControlsModalStore()
  const { t } = useTranslation()

  const infoItems = [
    {
      icon: faBullseyeArrow,
      boldText: t("STCM_INTRO_1_BOLD"),
      text: t("STCM_INTRO_1_TEXT"),
    },
    {
      icon: faUnlock,
      boldText: t("STCM_INTRO_2_BOLD"),
      text: t("STCM_INTRO_2_TEXT"),
    },
    {
      icon: faMobileAndroid,
      boldText: t("STCM_INTRO_3_BOLD"),
      text: t("STCM_INTRO_3_TEXT"),
    },
  ]

  const onContinue = () => {
    // note: we are not going to fire "screentime_instructions" event on first step.
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_SCREENTIME_INSTRUCTIONS, {
      modal_type: modalType,
    })
    setStep(ScreenTimeControlsModalStep.SETUP_PASSCODE)
  }

  const onDimiss = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_REMINDER_DISMISS)
    onClose()
    if (rewardId) {
      localStorage.setItem(
        localStorageItems.screentimeReminderDismissed + rewardId.toString(),
        "true"
      )
    }
  }

  return (
    <div className="w-full flex flex-col gap-2">
      <ScreenTimeControlsImage />

      <div className="w-full flex flex-col gap-4 p-6 pb-0">
        <AvaliableOnAndroidCard />
        <Typography variant="h3">{t("STCM_INTRO_TITLE")}</Typography>
        {infoItems.map((item, index) => (
          <div
            key={index}
            className="flex flex-row justify-start items-start gap-1"
          >
            <FontAwesomeIcon
              icon={item.icon}
              color={JoonUIColor.icon.accent}
              fontSize={SPACING.space4}
            />
            <Typography variant="bodySmallBold">
              {item.boldText}{" "}
              <Typography variant="bodySmall">{item.text}</Typography>
            </Typography>
          </div>
        ))}
      </div>

      <div className="w-full flex flex-col gap-2 p-6 pt-4">
        <Button
          text={t("STCM_INTRO_CONTINUE")}
          onClick={onContinue}
          fullWidth
        />
        <Button
          text={t("STCM_INTRO_NO_SETUP")}
          onClick={onDimiss}
          fullWidth
          buttonType="secondary"
        />
      </div>
    </div>
  )
}

const DeviceSelectionStep = () => {
  const { setStep, setIsIos } = useScreenTimeControlsModalStore()
  const { t } = useTranslation()

  const onContinue = (isIos: boolean) => {
    setIsIos(isIos)
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_INSTRUCTIONS, {
      step: ScreenTimeControlsAnalyticsStep.SETUP_JOONITY,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_SETUP_DEVICETYPE, {
      devicetype: isIos ? "ios" : "android",
    })
    setStep(ScreenTimeControlsModalStep.SETUP_JOONITY)
  }

  return (
    <div className="w-full flex flex-col gap-4 h-full px-6 pt-4 pb-6">
      <Typography variant="bodyBold">{t("STCM_DEVICE_TITLE")}</Typography>
      <Typography variant="bodySmall">{t("STCM_DEVICE_DESC")}</Typography>
      <Typography variant="bodySmallBold">
        {t("STCM_DEVICE_IOS_NOTE")}
      </Typography>
      <div className="w-full flex flex-row gap-2">
        <DeviceSelectionButton
          image="/images/logos/apple-icon-accent.png"
          text={t("STCM_DEVICE_IOS")}
          onClick={() => onContinue(true)}
        />
        <DeviceSelectionButton
          image="/images/logos/android-icon.png"
          text={t("STCM_DEVICE_ANDROID")}
          onClick={() => onContinue(false)}
        />
      </div>
      <ScreenTimeControlsTroubleshooting />
    </div>
  )
}

const SetupPasscodeStep = () => {
  const { setStep } = useScreenTimeControlsModalStore()
  const { user } = useUserQuery()
  const { t } = useTranslation()

  const onSkip = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_INSTRUCTIONS, {
      step: ScreenTimeControlsAnalyticsStep.DEVICE_SELECTION,
    })
    setStep(ScreenTimeControlsModalStep.DEVICE_SELECTION)
  }
  const hasParentPanelEnabled = user?.parent_panel_passcode !== null
  const hasParentAccessCodeEnabled =
    user?.account_passcode !== null || hasParentPanelEnabled

  return (
    <div className="w-full flex flex-col gap-4 p-6">
      <Typography variant="bodyBold">
        {hasParentAccessCodeEnabled
          ? t("STCM_PASSCODE_REVIEW")
          : t("STCM_PASSCODE_SETUP")}
      </Typography>
      <Typography variant="bodySmall">
        {hasParentPanelEnabled
          ? t("STCM_PASSCODE_DESC_PANEL")
          : hasParentAccessCodeEnabled
            ? t("STCM_PASSCODE_DESC_RECOMMEND")
            : t("STCM_PASSCODE_DESC_DEFAULT")}
      </Typography>
      <Typography variant="bodySmall">
        {t("STCM_PASSCODE_CODE_REQUIRED")}
      </Typography>
      <ManagePasscodeSection />
      <Button
        text={hasParentAccessCodeEnabled ? t("STCM_NEXT") : t("STCM_SKIP")}
        buttonType={hasParentAccessCodeEnabled ? "primary" : "secondary"}
        onClick={onSkip}
        fullWidth
      />
      <Typography variant="bodySmall">
        {t("STCM_PASSCODE_CHANGE_NOTE")}
      </Typography>
      <ScreenTimeControlsTroubleshooting />
    </div>
  )
}

const SetupJoonityStep = () => {
  enum KidAppSetupStep {
    OPEN_JOON_KID_APP,
    OPEN_SETTINGS,
    TAP_PARENT_PANEL,
    AUTHORIZE_SCREEN_TIME_CONTROLS,
    FOLLOW_INSTRUCTIONS,
  }

  const [isLoading, setIsLoading] = useState(false)
  const [isInitalLoadImage, setIsInitalLoadImage] = useState(true)
  const [currentStep, setCurrentStep] = useState<KidAppSetupStep>(
    KidAppSetupStep.OPEN_JOON_KID_APP
  )
  const {
    isIos,
    setStep: setScreenTimeControlsModalStep,
    setIsIos,
  } = useScreenTimeControlsModalStore()
  const { onOpen: onOpenGetChildStartedModal } = useGetChildStartedModal()
  const { t } = useTranslation()

  const setupSteps = [
    {
      text: t("STCM_JSTEP_1", { device: isIos ? "iOS" : "Android" }),
      image: `/images/screenTimeControlsSteps/${isIos ? "ios" : "android"}_step_1.png`,
    },
    {
      text: t("STCM_JSTEP_2"),
      image: "/images/screenTimeControlsSteps/step_2.png",
    },
    {
      text: t("STCM_JSTEP_3"),
      image: "/images/screenTimeControlsSteps/step_3.png",
    },
    {
      text: isIos ? t("STCM_JSTEP_4_IOS") : t("STCM_JSTEP_4_ANDROID"),
      image: `/images/screenTimeControlsSteps/${isIos ? "ios" : "android"}_step_4.png`,
    },
    {
      text: t("STCM_JSTEP_5"),
      image: "/images/screenTimeControlsSteps/step_5.png",
    },
  ]

  const onNext = async () => {
    setIsLoading(true)
    if (currentStep !== KidAppSetupStep.FOLLOW_INSTRUCTIONS) {
      try {
        // Wait for next image to load before going to next step
        const nextStep = (currentStep + 1) as KidAppSetupStep
        await preloadImage(setupSteps[nextStep].image)
        setCurrentStep(nextStep)
      } catch (error) {
        console.error("Failed to preload image:", error)
      } finally {
        setIsLoading(false)
      }
      return
    }
    await queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARDS])
    queryClient.invalidateQueries([QUERY_KEYS.CUSTOM_REWARD_BALANCES])
    queryClient.invalidateQueries([QUERY_KEYS.SCREEN_TIME_BALANCES])
    setIsLoading(false)
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_INSTRUCTIONS, {
      step: ScreenTimeControlsAnalyticsStep.FINISH,
    })
    setScreenTimeControlsModalStep(ScreenTimeControlsModalStep.FINISH)
  }

  const onSetUpAnotherDevice = () => {
    setCurrentStep(KidAppSetupStep.OPEN_JOON_KID_APP)
    setIsIos(!isIos)
  }

  return (
    <div className="w-full flex flex-col gap-4 p-6">
      <Typography variant="bodyBold">{t("STCM_JOONITY_TITLE")}</Typography>

      <div className="w-full flex flex-col gap-2 p-3 bg-white rounded-md border border-borderPrimary overflow-hidden items-center">
        <Typography
          variant="bodySmall"
          color={JoonUIColor.text.secondary}
          textAlign="center"
        >
          {t("STCM_STEP_OF", {
            current: currentStep + 1,
            total: setupSteps.length,
          })}
        </Typography>
        <Typography
          variant="bodySmall"
          style={{ whiteSpace: "pre-line" }}
          textAlign="center"
        >
          {setupSteps[currentStep].text}
        </Typography>
        <img
          src={setupSteps[currentStep].image}
          alt={`screen time controls step ${currentStep + 1}`}
          style={{
            width: "80%",
            minHeight: "100px",
            height: "auto",
            borderRadius: SPACING.space2,
            opacity: isInitalLoadImage ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
          onLoadStart={() => setIsInitalLoadImage(true)}
          onLoad={() => setIsInitalLoadImage(false)}
        />
        <div className="w-full flex flex-row gap-2">
          {currentStep !== KidAppSetupStep.OPEN_JOON_KID_APP && (
            <Button
              text={t("STCM_PREV_STEP")}
              buttonType="secondary"
              onClick={() =>
                setCurrentStep((currentStep - 1) as KidAppSetupStep)
              }
              fullWidth
            />
          )}

          {currentStep !== KidAppSetupStep.FOLLOW_INSTRUCTIONS && (
            <Button
              text={t("STCM_NEXT_STEP")}
              onClick={onNext}
              isLoading={isLoading}
              fullWidth
            />
          )}
        </div>
      </div>

      {currentStep === KidAppSetupStep.FOLLOW_INSTRUCTIONS && (
        <div className="w-full flex flex-col gap-2">
          <Button
            text={t("STCM_FINISH_SETUP")}
            onClick={onNext}
            isLoading={isLoading}
            fullWidth
          />
          <Button
            buttonType="secondary"
            text={t("STCM_SETUP_OTHER_DEVICE", {
              device: isIos ? "Android" : "iOS",
            })}
            onClick={onSetUpAnotherDevice}
            fullWidth
          />
        </div>
      )}

      {currentStep === KidAppSetupStep.OPEN_JOON_KID_APP ? (
        <button onClick={() => onOpenGetChildStartedModal()}>
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.primaryAccent}
          >
            {t("STCM_NO_KID_APP")}
          </Typography>
        </button>
      ) : (
        <ScreenTimeControlsTroubleshooting />
      )}
    </div>
  )
}

const FinishStep = () => {
  const { onClose, modalType, setStep, rewardId } =
    useScreenTimeControlsModalStore()
  const { t } = useTranslation()

  const { data: rewards, isLoading: isLoadingRewards } = useCustomRewardsQuery()
  const reward = rewards?.find((r) => r.id === rewardId)
  const isReminderModal = modalType === ScreenTimeControlsModalType.REWARDS_TAB
  const hasSetUpScreenTimeControls =
    reward && getScreenTimeConfigurations(reward).numLinkedDevices > 0

  const onClick = () => {
    if (hasSetUpScreenTimeControls) {
      onClose()
      return
    }
    setStep(ScreenTimeControlsModalStep.SETUP_PASSCODE)
  }

  const listItems = [
    t("STCM_FINISH_LIST_1"),
    t("STCM_FINISH_LIST_2"),
    t("STCM_FINISH_LIST_3"),
    t("STCM_FINISH_LIST_4"),
  ]

  const titleText = hasSetUpScreenTimeControls
    ? t("STCM_FINISH_SUCCESS_TITLE")
    : t("STCM_FINISH_FAIL_TITLE")

  const bodyText = !hasSetUpScreenTimeControls ? (
    t("STCM_FINISH_FAIL_BODY")
  ) : isReminderModal ? (
    t("STCM_FINISH_REMINDER_BODY")
  ) : (
    <>
      {t("STCM_FINISH_HOW")}
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {listItems.map((item, index) => (
          <li
            key={index}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <span style={{ width: SPACING.space3 }}>•</span>
            <Typography variant="bodySmall" textAlign="left">
              {item}
            </Typography>
          </li>
        ))}
      </ul>
    </>
  )

  useEffect(() => {
    if (isLoadingRewards) return
    if (hasSetUpScreenTimeControls) {
      trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_SETUP_SUCCESS)
    }
  }, [isLoadingRewards]) // eslint-disable-line

  return (
    <div className="w-full flex flex-col gap-2 justify-between h-full bg-white">
      <div className="w-full flex flex-col gap-4 p-6 pb-0">
        <Typography variant="bodyBold">{titleText}</Typography>
        {hasSetUpScreenTimeControls && (
          <Typography variant="bodySmall">
            {t("STCM_FINISH_EXCHANGE")}
          </Typography>
        )}
        <Typography variant="bodySmall" textAlign="left">
          {bodyText}
        </Typography>
        <Typography variant="bodySmall">
          {hasSetUpScreenTimeControls
            ? t("STCM_FINISH_NOTE_PREFIX_SUCCESS")
            : t("STCM_FINISH_NOTE_PREFIX_FAIL")}
          <a
            href="https://www.joonapp.io/user-manual/screen-time-controls"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography
              variant="bodySmallBold"
              style={{ color: JoonUIColor.text.primaryAccent }}
            >
              {t("STCM_TROUBLE_GUIDE")}
            </Typography>
          </a>
          {t("STCM_OR_EMAIL")}
          <a
            href="mailto:contact@joonapp.io"
            style={{ color: JoonUIColor.text.primaryAccent }}
          >
            contact@joonapp.io
          </a>
        </Typography>
      </div>
      <div
        style={{
          borderTop: `1px solid ${JoonUIColor.border.default}`,
          background: JoonUIColor.background.xlightGray,
          width: "100%",
          height: "fit-content",
          padding: SPACING.space6,
          paddingTop: SPACING.space4,
        }}
      >
        <Button
          text={
            hasSetUpScreenTimeControls ? t("STCM_GO_HOME") : t("STCM_TRY_AGAIN")
          }
          onClick={onClick}
          fullWidth
        />
      </div>
    </div>
  )
}

// Components

const ScreenTimeControlsImage = () => {
  return (
    <div
      className="w-full flex items-center justify-center  border-b border-borderPrimary"
      style={{
        userSelect: "none",
        pointerEvents: "none",
        borderBottom: `1px solid ${JoonUIColor.border.default}`,
      }}
    >
      <img
        src="/images/lock_apps.png"
        alt="screen time controls"
        style={{ width: "100%", height: "auto" }}
      />
    </div>
  )
}

const ScreenTimeControlsTroubleshooting = () => {
  const { t } = useTranslation()
  return (
    <Typography variant="bodySmall">
      {t("STCM_TROUBLE_TEXT")}
      <a
        href="https://www.joonapp.io/user-manual/screen-time-controls"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: JoonUIColor.text.primaryAccent }}
      >
        {t("STCM_TROUBLE_LINK")}
      </a>
    </Typography>
  )
}

const AvaliableOnAndroidCard = () => {
  const isMobile = useMobile()
  const { t } = useTranslation()

  return (
    <div className="w-full flex flex-row items-center">
      <div
        className="flex flex-1 gap-1 items-center justify-between"
        style={{
          height: "50px",
          border: `1px solid ${JoonColorExpanded.yellow300}`,
          borderRight: "none",
          background: JoonColorExpanded.yellow100,
          borderTopLeftRadius: SPACING.space2,
          borderBottomLeftRadius: SPACING.space2,
          padding: `${SPACING.space3} ${SPACING.space4}`,
        }}
      >
        <Typography
          variant={isMobile ? "bodySmallBold" : "bodyBold"}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t("STCM_AVAILABLE_ON")}
        </Typography>
        <img
          src="/images/logos/android-logo.svg"
          alt="Android"
          className="w-auto h-5"
        />
      </div>
      <div className="w-fit flex gap-4 items-center justify-center h-[50px] border border-yellow-400 rounded-r-md py-3 px-4 bg-yellow-300">
        <img
          src="/images/logos/android-icon.png"
          alt="Android"
          className="w-auto h-6"
        />
      </div>
    </div>
  )
}

const DeviceSelectionButton = ({
  image,
  text,
  onClick,
}: {
  image: string
  text: string
  onClick: () => void
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <button
      className="flex w-1/2 flex-col gap-1 items-center justify-center rounded-md !border !border-borderAccent !p-3"
      onClick={onClick}
      style={{
        background: isHovered
          ? JoonUIColor.background.lightGray
          : JoonUIColor.background.primaryNeutral,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img src={image} alt="Android" className="w-auto h-6" />
      <Typography variant="body" color={JoonUIColor.text.primaryAccent}>
        {text}
      </Typography>
    </button>
  )
}
